import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import {
  XMarkIcon,
  ArrowRightIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import AdminSidebar from "../../../components/AdminSidebar";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import PaginationV2 from "../../../components/PaginationV2";
import excelIcon from "../../../assets/excel-icon.webp";
import ColumnSearch from "../../../components/ColumnSearch";
import EditableSelectCell from "../../../components/EditableSelectCell";
import EditableDateCell from "../../../components/EditableDateCell";
import EditableCell from "../../../components/EditableCell";
import { toast } from "react-toastify";
import useDialogStore from "../../../stores/useDialogStore";
import ColumnSelector from "../../../components/ColumnSelector";
import EditableOptionsCell from "../../../components/EditableOptionsCell";
import ActionButtons from "../../../components/ActionButtons";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import dayjs from "dayjs";
import Button from "../../../components/Button";
import EditSerialNumberCell from "../../../components/EditSerialNumberCell";

export default function SparePartsStock() {
  const { openDialog, closeDialog } = useDialogStore();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const [selectedColumns, setSelectedColumns] = useState({
    unique_id: true,
    brand: true,
    model: true,
    serial_no: true,
    counter_black: true,
    counter_color: true,
    total_counter: true,
    palette: true,
    registered_date: true,
    options: true,
    spare_parts: true,
    comment: true,
  });

  // Update your column options to include all fields
  const columnOptions = [
    { key: "unique_id", label: "Unique ID" },
    { key: "brand", label: "Brand" },
    { key: "model", label: "Model" },
    { key: "serial_no", label: "Serial Number" },
    { key: "counter_black", label: "Counter Black" },
    { key: "counter_color", label: "Counter Color" },
    { key: "total_counter", label: "Total Counter" },
    { key: "palette", label: "Palette" },
    { key: "registered_date", label: "Registered Date" },
    { key: "options", label: "Device Options" },
    { key: "comment", label: "Comment" },
    { key: "spare_parts", label: "Spare Parts" },
  ];

  const ColumnSelectorContent = ({ onExport }) => {
    const [localSelectedColumns, setLocalSelectedColumns] =
      useState(selectedColumns);

    return (
      <div className="bg-white rounded-lg w-full p-4">
        <ColumnSelector
          columns={columnOptions}
          selectedColumns={localSelectedColumns}
          setSelectedColumns={setLocalSelectedColumns}
        />
        <div className="mt-6 flex justify-end space-x-2">
          <Button variant="light" size="sm" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              if (
                Object.values(localSelectedColumns).some((selected) => selected)
              ) {
                setSelectedColumns(localSelectedColumns);
                onExport(localSelectedColumns);
                closeDialog();
              } else {
                toast.warning("Please select at least one column to export");
              }
            }}
          >
            {exportMutation?.isPending ? (
              <div className="flex items-center gap-2">
                <div className="loader h-4 w-4 border-[2px] border-t-transparent border-white rounded-full animate-spin"></div>
                Loading...
              </div>
            ) : (
              "Export"
            )}
          </Button>
        </div>
      </div>
    );
  };

  const handleOpenColumnSelector = () => {
    openDialog({
      title: "Select Columns to Export",
      content: <ColumnSelectorContent onExport={handleExport} />,
    });
  };

  const exportMutation = useMutation({
    mutationFn: async (selectedColumnss) => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/export-excel/spare-parts-devices`,
        {
          columns: Object.entries(selectedColumnss)
            .filter(([_, selected]) => selected)
            .map(([key]) => key),
          params: params.toString(),
        },
        {
          responseType: "blob",
        }
      );
      return response;
    },
    onSuccess: (response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `spare_parts_stock_export_${
          new Date().toISOString().split("T")[0]
        }.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      closeDialog();
      toast.success("Spare Parts Stock Data exported successfully!");
    },
    onError: (error) => {
      console.error("Export error:", error);
      toast.error("Failed to export report. Please try again.");
    },
  });

  const handleExport = async (selectedColumnss) => {
    if (Object.values(selectedColumnss).some((selected) => selected)) {
      exportMutation.mutate(selectedColumnss);
    } else {
      toast.warning("Please select at least one column to export");
    }
  };

  const sortConfig = {
    key: null,
    direction: "desc",
  };

  const user = getUser();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const editCellMutation = useMutation({
    mutationFn: ({ deviceId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/devices/patch/${deviceId}`,
        { [field]: value, user_id: user.id }
      ),
    onSuccess: () => {
      refetch();
      toast.success("Device updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
      console.error("Update device error:", error);
    },
  });

  const handleMoveToRecycleMutation = useMutation({
    mutationFn: async (device_option_id) => {
      const currentDateTime = new Date()
        .toISOString()
        .slice(0, 19)
        .replace("T", " ");
      console.log("Current Date and Time:", currentDateTime);
      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/update-recycled-date`,
        {
          device_option_id: device_option_id,
          recycled_date: currentDateTime,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["sparePartsDevices"]);
      toast.success("Device recycled date updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update recycled date. Please try again.");
      console.error("Update recycled date error:", error);
    },
  });

  const editSparePartsMutation = useMutation({
    mutationFn: ({ deviceId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/device-options/patch/${deviceId}`,
        { [field]: value }
      ),
    onSuccess: () => {
      refetch();
      toast.success("Device updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
      console.error("Update device error:", error);
    },
  });

  const MoveToRecycleConfirmation = ({
    deviceOptionId,
    onConfirm,
    onCancel,
  }) => {
    return (
      <div className="bg-white rounded-lg w-full p-4">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
            <ArrowRightIcon className="h-6 w-6 text-yellow-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Move to Recycle Stock
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            Are you sure you want to move this device to recycle stock? This
            action cannot be undone.
          </p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={handleMoveToRecycleMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {handleMoveToRecycleMutation.isPending
              ? "Moving..."
              : "Move to Recycle"}
          </button>
        </div>
      </div>
    );
  };

  const handleMoveToRecycle = (deviceOptionId) => {
    openDialog({
      title: "",
      content: (
        <MoveToRecycleConfirmation
          deviceOptionId={deviceOptionId}
          onConfirm={() => {
            handleMoveToRecycleMutation.mutate(deviceOptionId);
            closeDialog();
          }}
          onCancel={closeDialog}
        />
      ),
    });
  };

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () =>
      axiosInstance.get(`${window.REACT_APP_API_ENDPOINT}/device-brands`),
    select: (data) => data.data.data.map(({ id, name }) => ({ id, name })),
  }).data;

  const models = useQuery({
    queryKey: ["models"],
    queryFn: () =>
      axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-models?type=all`
      ),
    select: (data) =>
      data.data.data.data.map(({ id, name, device_brand_id }) => ({
        id,
        name,
        device_brand_id,
      })),
  }).data;

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/users`
      );
      return response.data.data.map((user) => ({
        id: user.id,
        name: `${user.name} ${user.last_name}`,
      }));
    },
  });

  const {
    data: devicesData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["sparePartsDevices", location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/spare-parts?${params.toString()}`
      );
      console.log(response);
      return response.data;
    },
    keepPreviousData: true,
  });

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page");
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    navigate(`${location.pathname}?${params.toString()}`);
  };

  const handleClearFilters = () => {
    navigate(location.pathname);
  };

  const deleteDeviceMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.delete(`${window.REACT_APP_API_ENDPOINT}/devices/${id}`),
    onSuccess: () => {
      refetch();
      toast.success("Device deleted successfully!");
    },
    onError: (error) => {
      console.error("Delete device error:", error);
    },
  });
  const handleDelete = (id) => {
    openDialog({
      title: "Delete Device",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to delete this device?
            <br />
            <span className="text-red-500 text-sm font-bold">
              (It will be moved to the trash bin)
            </span>
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={<TrashIcon className="h-4 w-4" />}
              onClick={() => {
                deleteDeviceMutation.mutate(id);
                closeDialog();
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ),
    });
  };

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
          </div>
        </div>
        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex justify-between items-center py-3 px-4 bg-white rounded-lg">
                <h4 className="font-semibold text-2xl">Spare Parts Stock</h4>
                <div className="flex flex-col gap-2 lg:flex-row items-center">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<XMarkIcon className="h-4 w-4" />}
                    onClick={handleClearFilters}
                    className="mb-2 lg:mb-0"
                  >
                    Clear Filters
                  </Button>
                  <Button
                    variant="outline-success"
                    size="sm"
                    icon={excelIcon}
                    onClick={handleOpenColumnSelector}
                    className="mb-2 lg:mb-0"
                  >
                    Export Spare Parts Data as CSV
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="lg:min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th className="px-4 py-4 text-center text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Unique ID"
                            columnKey="unique_id"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Unique ID..."
                          />
                        </div>
                      </th>

                      <th className="px-4 py-4 text-center text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Brand"
                            columnKey="brand"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Brand..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-center text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Model"
                            columnKey="model"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Model..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-center text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Serial Number"
                            columnKey="serial_no"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Serial Number..."
                          />
                        </div>
                      </th>

                      <th className="px-4 py-4 text-center text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Counter Color"
                            columnKey="counter_color"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Counter Color..."
                          />
                        </div>
                      </th>

                      <th className="px-4 py-4 text-center text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Counter Black"
                            columnKey="counter_black"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Counter Black..."
                          />
                        </div>
                      </th>

                      <th className="px-4 py-4 text-center text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Total Counter"
                            columnKey="total_counter"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Total Counter..."
                          />
                        </div>
                      </th>

                      <th className="px-4 py-4 text-center text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Palette"
                            columnKey="palette"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Palette..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-center text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Registered Date"
                            columnKey="registered_date"
                            isDate={true}
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Registered Date..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-center text-white text-sm">
                        Options
                      </th>

                      <th className="px-4 py-4 text-center text-white text-sm">
                        Comment
                      </th>
                      {/* 
                      <th className="px-4 py-4 text-center text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="User"
                            columnKey="user"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by User..."
                          />
                        </div>
                      </th> */}

                      <th className="px-4 py-4 text-center text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Spare Parts"
                            columnKey="spare_parts"
                            isDate={true}
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Spare Parts Date..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-center text-white text-sm">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {isLoading ? (
                      <tr>
                        <td colSpan={13} className="text-center">
                          <LoadingComponent />
                        </td>
                      </tr>
                    ) : devicesData?.data.length == 0 ? (
                      <tr>
                        <td colSpan={13} className="text-center">
                          <NoData message="No spare parts devices found" />
                        </td>
                      </tr>
                    ) : (
                      devicesData?.data.map((device, index) => (
                        <tr
                          key={device.id}
                          className={`divide-y divide-gray-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-gray-100"
                          }`}
                        >
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            {device.unique_id}
                          </td>

                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              {brands && (
                                <EditableSelectCell
                                  value={device.brand}
                                  displayValue={device.brand}
                                  id={device.device_id}
                                  options={brands}
                                  field="brand"
                                  onSave={(data) =>
                                    editCellMutation.mutate({
                                      deviceId: data.id,
                                      field: "device_brand_id",
                                      value: data.value,
                                    })
                                  }
                                />
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            {models && (
                              <EditableSelectCell
                                value={device.model}
                                displayValue={device.model}
                                id={device.device_id}
                                options={models.filter(
                                  (model) =>
                                    model.device_brand_id ===
                                    device.device_brand_id
                                )}
                                field="model"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "device_model_id",
                                    value: data.value,
                                  })
                                }
                              />
                            )}
                          </td>
                          <td className="whitespace-nowrap text-left p-3 text-sm text-black">
                            <EditSerialNumberCell
                              value={device.serial_no}
                              id={device.device_id}
                              field="serial_no"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "serial_no",
                                  value: data.value,
                                })
                              }
                            />
                          </td>

                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={device.counter_color}
                              id={device.device_id}
                              field="counter_color"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "counter_color",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={device.counter_black}
                              id={device.device_id}
                              field="counter_black"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "counter_black",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={device.total_counter}
                              id={device.device_id}
                              field="total_counter"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "total_counter",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={device.palette}
                              id={device.device_id}
                              field="palette"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "palette",
                                  value: data.value,
                                })
                              }
                            />
                          </td>

                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            {dayjs.utc(device.registered_date).format("DD-MM-YYYY")}
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableOptionsCell
                              deviceId={device.device_id}
                              deviceOptions={device.device_options}
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={device.comment}
                              id={device.device_id}
                              field="comment"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "comment",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          {/* 
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            {users && (
                              <EditableSelectCell
                                value={device.user}
                                displayValue={device.user}
                                id={device.device_id}
                                options={users}
                                field="user"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "user_id",
                                    value: data.value,
                                  })
                                }
                              />
                            )}
                          </td> */}
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            {device.device_options && (
                              <EditableDateCell
                                value={device.device_options.spare_parts}
                                displayValue={device.device_options.spare_parts}
                                id={device.device_id}
                                field="spare_parts"
                                onSave={(data) =>
                                  editSparePartsMutation.mutate({
                                    deviceId: data.id,
                                    field: "spare_parts",
                                    value: data.value,
                                  })
                                }
                              />
                            )}
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black flex items-center gap-2">
                            {device.device_options && (
                              <button
                                onClick={() =>
                                  handleMoveToRecycle(device.device_options.id)
                                }
                                disabled={handleMoveToRecycleMutation.isPending}
                                className="bg-blue-100 text-blue-800 hover:bg-blue-200 duration-200 text-sm rounded-md px-3 py-2 font-bold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <ArrowRightIcon className="h-4 w-4" />
                                {handleMoveToRecycleMutation.isPending
                                  ? "Moving..."
                                  : "Move to Recycle Stock"}
                              </button>
                            )}
                            <ActionButtons
                              id={device.device_id}
                              onDelete={() => handleDelete(device.device_id)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationV2
                current_page={devicesData?.current_page}
                last_page={devicesData?.last_page}
                next_page_url={devicesData?.next_page_url}
                prev_page_url={devicesData?.prev_page_url}
              />
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}
